import React, { useState } from 'react';
import { Card, CardBody, Button, MarkdownRenderer } from '../common';

const SubmissionItem = ({ submission, onAddContent, onViewFull, addedContent }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user, prose, images, audio, referencedAspects } = submission;

  // Check if specific content has already been added
  const isProseAdded = addedContent.some(
    c => c.submissionId === submission.id && c.contentType === 'prose'
  );

  // Track which images have been added by their storage key or URL
  const addedImageKeys = addedContent
    .filter(c => c.submissionId === submission.id && c.contentType === 'image')
    .map(c => c.mediaKey || c.mediaUrl);

  // Track which audio have been added
  const addedAudioKeys = addedContent
    .filter(c => c.submissionId === submission.id && c.contentType === 'audio')
    .map(c => c.mediaKey || c.mediaUrl);

  const hasAnyContent = prose?.content || images?.length > 0 || audio?.length > 0;

  if (!hasAnyContent) return null;

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-gray-900">{user.displayName}</span>
          <Button
            variant="link"
            size="sm"
            onClick={() => onViewFull(submission)}
          >
            View Full
          </Button>
        </div>

        {/* Referenced Aspects */}
        {referencedAspects?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {referencedAspects.map((aspect, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700"
                title={aspect.aspectName}
              >
                {aspect.aspectName}
              </span>
            ))}
          </div>
        )}

        {/* Prose content */}
        {prose?.content && (
          <div className="mb-3">
            <div className="relative">
              <div className={`text-sm text-gray-700 ${!isExpanded ? 'line-clamp-3' : ''}`}>
                <MarkdownRenderer content={prose.content} />
              </div>

              {/* Expand/collapse button */}
              {prose.content.length > 150 && (
                <button
                  className="text-xs text-primary-600 hover:text-primary-800 mt-1 font-medium"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>

            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              disabled={isProseAdded}
              onClick={() => onAddContent({
                type: 'player_content',
                submissionId: submission.id,
                userId: user.id,
                content: prose.content,
                contentType: 'prose',
                referencedAspects: submission.referencedAspects
              })}
            >
              {isProseAdded ? 'Text Added' : '+ Add Text to Passage'}
            </Button>
          </div>
        )}

        {/* Images */}
        {images?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-2">Images ({images.length})</p>
            <div className="flex flex-wrap gap-2">
              {images.map((img, idx) => {
                const isImageAdded = addedImageKeys.includes(img.storageKey) || addedImageKeys.includes(img.url);
                return (
                  <div key={idx} className="relative">
                    <img
                      src={img.url}
                      alt={img.originalFilename}
                      className={`h-20 w-20 object-cover rounded border ${isImageAdded ? 'opacity-50' : ''}`}
                    />
                    <div className="mt-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isImageAdded}
                        onClick={() => onAddContent({
                          type: 'player_content',
                          submissionId: submission.id,
                          userId: user.id,
                          content: img.originalFilename,
                          contentType: 'image',
                          mediaUrl: img.url,
                          mediaKey: img.storageKey,
                          referencedAspects: submission.referencedAspects
                        })}
                      >
                        {isImageAdded ? 'Added' : '+ Add'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Audio */}
        {audio?.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-500">Audio ({audio.length})</p>
            {audio.map((aud, idx) => {
              const isAudioAdded = addedAudioKeys.includes(aud.storageKey) || addedAudioKeys.includes(aud.url);
              return (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 flex-1">
                    {aud.originalFilename} ({Math.round(aud.durationSeconds)}s)
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isAudioAdded}
                    onClick={() => onAddContent({
                      type: 'player_content',
                      submissionId: submission.id,
                      userId: user.id,
                      content: aud.originalFilename,
                      contentType: 'audio',
                      mediaUrl: aud.url,
                      mediaKey: aud.storageKey,
                      referencedAspects: submission.referencedAspects
                    })}
                  >
                    {isAudioAdded ? 'Added' : '+ Add to Passage'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export const SubmissionPicker = ({ submissions, onAddContent, onViewFull, addedContent = [] }) => {
  if (!submissions || submissions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No submissions received yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-semibold mb-3">Player Submissions</h3>
      <div className="max-h-[500px] overflow-y-auto pr-2">
        {submissions.map((submission) => (
          <SubmissionItem
            key={submission.id}
            submission={submission}
            onAddContent={onAddContent}
            onViewFull={onViewFull}
            addedContent={addedContent}
          />
        ))}
      </div>
    </div>
  );
};
